import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";
import Layout from "../components/Layout";
import ProductProfitReportPanel from "./ProductProfitReportPanel";
import "./ProfitCostPage.css";

const emptyItem = {
  productCode: "",
  color: "",
  size: "",
  expectedQty: 0,
  receivedQty: 0,
  lostQty: 0,
  damagedQty: 0,
  unitCostUsd: 0,
  targetSellingPriceMmk: 0,
};

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

function formatPercent(value) {
  const n = Number(value || 0);
  return `${n.toFixed(2)}%`;
}

function newBatchNo() {
  const d = new Date();
  const ymd = d.toISOString().slice(0, 10).replaceAll("-", "");
  return `IMPORT-${ymd}-${Date.now().toString().slice(-5)}`;
}

export default function ProfitCostPage({ embedded = false }) {
  const [summary, setSummary] = useState({});
  const [batches, setBatches] = useState([]);
  const [saveResult, setSaveResult] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [claimExpected, setClaimExpected] = useState(0);
  const [claimReceived, setClaimReceived] = useState(0);
  const [claimSaving, setClaimSaving] = useState(false);

  const [form, setForm] = useState({
    batchNo: newBatchNo(),
    brand: "HANGTEN",
    supplierName: "",
    invoiceNo: "",
    invoiceDate: new Date().toISOString().slice(0, 10),

    orderExchangeRate: 0,
    paymentExchangeRate: 0,
    landedExchangeRate: 0,

    freightUsd: 0,
    dutyMmk: 0,
    otherChargesMmk: 0,

    compensationExpectedMmk: 0,
    compensationReceivedMmk: 0,
    note: "",
  });

  const [items, setItems] = useState([
    { ...emptyItem },
    { ...emptyItem },
  ]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  async function loadData() {
    try {
      setLoading(true);

      const [summaryRes, batchRes] = await Promise.all([
        fetch(`${API_BASE}/api/erp/import-cost-summary`),
        fetch(`${API_BASE}/api/erp/import-cost-batches`),
      ]);

      const summaryData = await summaryRes.json();
      const batchData = await batchRes.json();

      setSummary(summaryData.summary || {});
      setBatches(batchData.rows || []);
    } catch (err) {
      alert(err.message || "Failed to load profit cost data");
    } finally {
      setLoading(false);
    }
  }

  async function loadBatchDetail(batchId) {
    try {
      setDetailLoading(true);

      const res = await fetch(`${API_BASE}/api/erp/import-cost-batches/${batchId}`);
      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to load batch detail");
      }

      setSelectedBatch(data.batch || null);
      setSelectedItems(data.items || []);

      setClaimExpected(Number(data.batch?.compensation_expected_mmk || 0));
      setClaimReceived(Number(data.batch?.compensation_received_mmk || 0));
    } catch (err) {
      alert(err.message || "Failed to load batch detail");
    } finally {
      setDetailLoading(false);
    }
  }

  async function updateCompensation() {
    if (!selectedBatch?.id) return;

    try {
      setClaimSaving(true);

      const res = await fetch(
        `${API_BASE}/api/erp/import-cost-batches/${selectedBatch.id}/compensation`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            compensationExpectedMmk: Number(claimExpected || 0),
            compensationReceivedMmk: Number(claimReceived || 0),
          }),
        },
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to update compensation");
      }

      setSelectedBatch(data.batch);
      setClaimExpected(Number(data.batch?.compensation_expected_mmk || 0));
      setClaimReceived(Number(data.batch?.compensation_received_mmk || 0));

      await loadData();

      alert("Compensation updated ✅");
    } catch (err) {
      alert(err.message || "Compensation update failed");
    } finally {
      setClaimSaving(false);
    }
  }

  async function deleteImportBatch(batch) {
    const ok = window.confirm(
      `Delete import batch "${batch.batch_no}"?\n\nThis will also delete all items under this batch.`,
    );

    if (!ok) return;

    try {
      const res = await fetch(
        `${API_BASE}/api/erp/import-cost-batches/${batch.id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            confirmText: batch.batch_no,
          }),
        },
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to delete import batch");
      }

      if (selectedBatch?.id === batch.id) {
        setSelectedBatch(null);
        setSelectedItems([]);
      }

      await loadData();

      alert("Import batch deleted ✅");
    } catch (err) {
      alert(err.message || "Delete failed");
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  function updateForm(name, value) {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function updateItem(index, name, value) {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              [name]: value,
            }
          : item,
      ),
    );
  }

  function addItem() {
    setItems((prev) => [...prev, { ...emptyItem }]);
  }

  function removeItem(index) {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }

  const localPreview = useMemo(() => {
    const orderRate = Number(form.orderExchangeRate || 0);
    const paymentRate = Number(form.paymentExchangeRate || 0);
    const landedRate =
      Number(form.landedExchangeRate || 0) || paymentRate || orderRate;

    const productCostUsd = items.reduce((sum, item) => {
      return sum + Number(item.expectedQty || 0) * Number(item.unitCostUsd || 0);
    }, 0);

    const freightUsd = Number(form.freightUsd || 0);
    const dutyMmk = Number(form.dutyMmk || 0);
    const otherChargesMmk = Number(form.otherChargesMmk || 0);

    const totalCostMmk =
      productCostUsd * landedRate +
      freightUsd * landedRate +
      dutyMmk +
      otherChargesMmk;

    const expectedQty = items.reduce(
      (sum, item) => sum + Number(item.expectedQty || 0),
      0,
    );

    const lostQty = items.reduce(
      (sum, item) => sum + Number(item.lostQty || 0),
      0,
    );

    const damagedQty = items.reduce(
      (sum, item) => sum + Number(item.damagedQty || 0),
      0,
    );

    const receivedQty = items.reduce((sum, item) => {
      const received =
        item.receivedQty !== "" && item.receivedQty !== null
          ? Number(item.receivedQty || 0)
          : Math.max(Number(item.expectedQty || 0) - Number(item.lostQty || 0), 0);

      return sum + received;
    }, 0);

    const fxGainLossMmk =
      productCostUsd *
      ((paymentRate || landedRate || 0) - (orderRate || paymentRate || landedRate || 0));

    return {
      productCostUsd,
      totalCostMmk,
      expectedQty,
      receivedQty,
      lostQty,
      damagedQty,
      fxGainLossMmk,
    };
  }, [form, items]);

  function createCostFromMissingProduct(row) {
    const qty = Number(row?.qty || 0);
    const unitSalesPrice = Number(row?.unitSalesPrice || 0);

    setForm((prev) => ({
      ...prev,
      batchNo: newBatchNo(),
      supplierName: "",
      invoiceNo: "",
      note: `Created from missing cost product: ${row?.productCode || ""} / ${row?.color || ""} / ${row?.size || ""}`,
    }));

    setItems([
      {
        productCode: row?.productCode || "",
        color: row?.color || "",
        size: row?.size || "",
        expectedQty: qty,
        receivedQty: qty,
        lostQty: 0,
        damagedQty: 0,
        unitCostUsd: 0,
        targetSellingPriceMmk: Math.round(unitSalesPrice),
      },
    ]);

    setTimeout(() => {
      const formEl = document.querySelector(".profitCostForm");
      if (formEl) {
        formEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  }

  async function saveBatch() {
    try {
      setSaving(true);
      setSaveResult(null);

      const cleanItems = items
        .filter((item) => String(item.productCode || "").trim())
        .map((item) => ({
          ...item,
          expectedQty: Number(item.expectedQty || 0),
          receivedQty: Number(item.receivedQty || 0),
          lostQty: Number(item.lostQty || 0),
          damagedQty: Number(item.damagedQty || 0),
          unitCostUsd: Number(item.unitCostUsd || 0),
          targetSellingPriceMmk: Number(item.targetSellingPriceMmk || 0),
        }));

      if (cleanItems.length === 0) {
        alert("Product item အနည်းဆုံး ၁ ခုထည့်ပါ");
        return;
      }

      const payload = {
        ...form,
        orderExchangeRate: Number(form.orderExchangeRate || 0),
        paymentExchangeRate: Number(form.paymentExchangeRate || 0),
        landedExchangeRate: Number(form.landedExchangeRate || 0),
        freightUsd: Number(form.freightUsd || 0),
        dutyMmk: Number(form.dutyMmk || 0),
        otherChargesMmk: Number(form.otherChargesMmk || 0),
        compensationExpectedMmk: Number(form.compensationExpectedMmk || 0),
        compensationReceivedMmk: Number(form.compensationReceivedMmk || 0),
        items: cleanItems,
      };

      const res = await fetch(`${API_BASE}/api/erp/import-cost-batches`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save import cost batch");
      }

      setSaveResult(data);
      setForm((prev) => ({
        ...prev,
        batchNo: newBatchNo(),
        invoiceNo: "",
        note: "",
      }));
      setItems([{ ...emptyItem }, { ...emptyItem }]);

      await loadData();
    } catch (err) {
      alert(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const content = (
    <div className="profitCost">
      <div className="profitCost__hero">
        <div>
          <span>Profit Cost Module</span>
          <h1>Import Cost & Profit Analysis</h1>
          <p>
            USD buying cost, exchange rate, freight, loss, damage, compensation
            and landed cost control for Hangten / PrettyFit products.
          </p>
        </div>

        <button onClick={loadData} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="profitCost__summary">
        <div className="profitCostCard">
          <span>Batches</span>
          <strong>{formatMoney(summary.batch_count)}</strong>
        </div>

        <div className="profitCostCard">
          <span>Product Cost USD</span>
          <strong>{formatMoney(summary.product_cost_usd)} USD</strong>
        </div>

        <div className="profitCostCard">
          <span>Freight USD</span>
          <strong>{formatMoney(summary.freight_usd)} USD</strong>
        </div>

        <div className="profitCostCard profitCostCard--danger">
          <span>Lost Cost</span>
          <strong>{formatMoney(summary.lost_cost_mmk)} Ks</strong>
        </div>

        <div className="profitCostCard profitCostCard--warn">
          <span>Damage Cost</span>
          <strong>{formatMoney(summary.damaged_cost_mmk)} Ks</strong>
        </div>

        <div className="profitCostCard profitCostCard--good">
          <span>Compensation Received</span>
          <strong>{formatMoney(summary.compensation_received_mmk)} Ks</strong>
        </div>
      </div>

      <ProductProfitReportPanel onCreateCostFromMissing={createCostFromMissingProduct} />

      <div className="profitCost__grid">
        <div className="profitCostForm">
          <div className="profitCostSectionTitle">
            <span>New Batch</span>
            <h2>Create Import Cost Batch</h2>
          </div>

          <div className="profitCostForm__grid">
            <label>
              Batch No
              <input
                value={form.batchNo}
                onChange={(e) => updateForm("batchNo", e.target.value)}
              />
            </label>

            <label>
              Brand
              <select
                value={form.brand}
                onChange={(e) => updateForm("brand", e.target.value)}
              >
                <option value="HANGTEN">HANGTEN</option>
                <option value="PRETTYFIT">PRETTYFIT</option>
                <option value="OTHER">OTHER</option>
              </select>
            </label>

            <label>
              Supplier Name
              <input
                value={form.supplierName}
                onChange={(e) => updateForm("supplierName", e.target.value)}
              />
            </label>

            <label>
              Invoice No
              <input
                value={form.invoiceNo}
                onChange={(e) => updateForm("invoiceNo", e.target.value)}
              />
            </label>

            <label>
              Invoice Date
              <input
                type="date"
                value={form.invoiceDate}
                onChange={(e) => updateForm("invoiceDate", e.target.value)}
              />
            </label>

            <label>
              Order Rate
              <input
                type="number"
                value={form.orderExchangeRate}
                onChange={(e) => updateForm("orderExchangeRate", e.target.value)}
              />
            </label>

            <label>
              Payment Rate
              <input
                type="number"
                value={form.paymentExchangeRate}
                onChange={(e) => updateForm("paymentExchangeRate", e.target.value)}
              />
            </label>

            <label>
              Landed Rate
              <input
                type="number"
                value={form.landedExchangeRate}
                onChange={(e) => updateForm("landedExchangeRate", e.target.value)}
              />
            </label>

            <label>
              Freight USD
              <input
                type="number"
                value={form.freightUsd}
                onChange={(e) => updateForm("freightUsd", e.target.value)}
              />
            </label>

            <label>
              Duty MMK
              <input
                type="number"
                value={form.dutyMmk}
                onChange={(e) => updateForm("dutyMmk", e.target.value)}
              />
            </label>

            <label>
              Other Charges MMK
              <input
                type="number"
                value={form.otherChargesMmk}
                onChange={(e) => updateForm("otherChargesMmk", e.target.value)}
              />
            </label>

            <label>
              Claim Expected MMK
              <input
                type="number"
                value={form.compensationExpectedMmk}
                onChange={(e) =>
                  updateForm("compensationExpectedMmk", e.target.value)
                }
              />
            </label>

            <label>
              Claim Received MMK
              <input
                type="number"
                value={form.compensationReceivedMmk}
                onChange={(e) =>
                  updateForm("compensationReceivedMmk", e.target.value)
                }
              />
            </label>
          </div>

          <label className="profitCostForm__note">
            Note
            <textarea
              value={form.note}
              onChange={(e) => updateForm("note", e.target.value)}
            />
          </label>

          <div className="profitCostPreview">
            <div>
              <span>Preview Total Cost</span>
              <strong>{formatMoney(localPreview.totalCostMmk)} Ks</strong>
            </div>
            <div>
              <span>FX Gain / Loss</span>
              <strong>{formatMoney(localPreview.fxGainLossMmk)} Ks</strong>
            </div>
            <div>
              <span>Expected / Received</span>
              <strong>
                {formatMoney(localPreview.expectedQty)} /{" "}
                {formatMoney(localPreview.receivedQty)}
              </strong>
            </div>
            <div>
              <span>Lost / Damage Qty</span>
              <strong>
                {formatMoney(localPreview.lostQty)} /{" "}
                {formatMoney(localPreview.damagedQty)}
              </strong>
            </div>
          </div>
        </div>

        <div className="profitCostList">
          <div className="profitCostSectionTitle">
            <span>Saved Batches</span>
            <h2>Recent Import Batches</h2>
          </div>

          <div className="profitCostBatchList">
            {batches.length === 0 ? (
              <div className="profitCostEmpty">No import batch yet.</div>
            ) : (
              batches.map((batch) => (
                <div key={batch.id} className="profitCostBatch">
                  <div>
                    <strong>{batch.batch_no}</strong>
                    <small>
                      {batch.brand} | {batch.supplier_name || "-"}
                    </small>
                  </div>
                  <div>
                    <b>{formatMoney(batch.product_cost_usd)} USD</b>
                    <small>
                      Lost {formatMoney(batch.lost_qty)} | Damage{" "}
                      {formatMoney(batch.damaged_qty)}
                    </small>
                  </div>
                  <span>{batch.compensation_status}</span>
                  <button
                    className="profitCostViewBtn"
                    onClick={() => loadBatchDetail(batch.id)}
                    disabled={detailLoading}
                  >
                    View Detail
                  </button>

                  <button
                    className="profitCostDeleteBtn"
                    onClick={() => deleteImportBatch(batch)}
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {selectedBatch && (
        <div className="profitCostDetail">
          <div className="profitCostDetail__head">
            <div>
              <span>Batch Detail</span>
              <h2>{selectedBatch.batch_no}</h2>
              <p>
                {selectedBatch.brand} | Supplier: {selectedBatch.supplier_name || "-"} | Invoice:{" "}
                {selectedBatch.invoice_no || "-"}
              </p>
            </div>

            <button
              onClick={() => {
                setSelectedBatch(null);
                setSelectedItems([]);
              }}
            >
              Close
            </button>
          </div>

          <div className="profitCostDetail__summary">
            <div>
              <span>Product Cost</span>
              <strong>{formatMoney(selectedBatch.product_cost_usd)} USD</strong>
            </div>
            <div>
              <span>Freight</span>
              <strong>{formatMoney(selectedBatch.freight_usd)} USD</strong>
            </div>
            <div>
              <span>FX Gain / Loss</span>
              <strong>{formatMoney(selectedBatch.fx_gain_loss_mmk)} Ks</strong>
            </div>
            <div>
              <span>Lost Cost</span>
              <strong>{formatMoney(selectedBatch.lost_cost_mmk)} Ks</strong>
            </div>
            <div>
              <span>Damage Cost</span>
              <strong>{formatMoney(selectedBatch.damaged_cost_mmk)} Ks</strong>
            </div>
            <div>
              <span>Claim Status</span>
              <strong>{selectedBatch.compensation_status || "-"}</strong>
            </div>
          </div>

          <div className="profitCostClaimBox">
            <div>
              <span>Claim Expected MMK</span>
              <input
                type="number"
                value={claimExpected}
                onChange={(e) => setClaimExpected(e.target.value)}
              />
            </div>

            <div>
              <span>Claim Received MMK</span>
              <input
                type="number"
                value={claimReceived}
                onChange={(e) => setClaimReceived(e.target.value)}
              />
            </div>

            <div>
              <span>Current Status</span>
              <strong>{selectedBatch.compensation_status || "-"}</strong>
            </div>

            <button onClick={updateCompensation} disabled={claimSaving}>
              {claimSaving ? "Saving..." : "Update Claim"}
            </button>
          </div>

          <div className="profitCostDetailTableWrap">
            <table className="profitCostDetailTable">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Color</th>
                  <th>Size</th>
                  <th>Sellable Qty</th>
                  <th>Lost</th>
                  <th>Damage</th>
                  <th>Unit USD</th>
                  <th>Landed Cost</th>
                  <th>Sell Price</th>
                  <th>Profit / Unit</th>
                  <th>Total Profit</th>
                  <th>Margin</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {selectedItems.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="profitCostDetailEmpty">
                      No item detail.
                    </td>
                  </tr>
                ) : (
                  selectedItems.map((item) => {
                    const landed = Number(item.unit_landed_cost_mmk || 0);
                    const sellPrice = Number(item.target_selling_price_mmk || 0);
                    const sellableQty = Number(item.sellable_qty || 0);
                    const profitPerUnit = sellPrice - landed;
                    const totalProfit = profitPerUnit * sellableQty;
                    const margin = sellPrice > 0 ? (profitPerUnit / sellPrice) * 100 : 0;

                    return (
                      <tr key={item.id}>
                        <td><b>{item.product_code}</b></td>
                        <td>{item.color || "-"}</td>
                        <td>{item.size || "-"}</td>
                        <td>{formatMoney(sellableQty)}</td>
                        <td>{formatMoney(item.lost_qty)}</td>
                        <td>{formatMoney(item.damaged_qty)}</td>
                        <td>{formatMoney(item.unit_cost_usd)} USD</td>
                        <td>{formatMoney(landed)} Ks</td>
                        <td>{formatMoney(sellPrice)} Ks</td>
                        <td className={profitPerUnit >= 0 ? "profitGood" : "profitBad"}>
                          {formatMoney(profitPerUnit)} Ks
                        </td>
                        <td className={totalProfit >= 0 ? "profitGood" : "profitBad"}>
                          {formatMoney(totalProfit)} Ks
                        </td>
                        <td className={margin >= 20 ? "profitGood" : margin >= 0 ? "profitWarn" : "profitBad"}>
                          {formatPercent(margin)}
                        </td>
                        <td>
                          <span className="profitStatus">{item.item_status || "NORMAL"}</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="profitCostItems">
        <div className="profitCostItems__head">
          <div className="profitCostSectionTitle">
            <span>Items</span>
            <h2>Product Cost Items</h2>
          </div>

          <button onClick={addItem}>+ Add Item</button>
        </div>

        <div className="profitCostItemTableWrap">
          <table className="profitCostItemTable">
            <thead>
              <tr>
                <th>Code</th>
                <th>Color</th>
                <th>Size</th>
                <th>Expected</th>
                <th>Received</th>
                <th>Lost</th>
                <th>Damage</th>
                <th>Unit USD</th>
                <th>Sell Price MMK</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {items.map((item, index) => (
                <tr key={index}>
                  <td>
                    <input
                      value={item.productCode}
                      onChange={(e) =>
                        updateItem(index, "productCode", e.target.value)
                      }
                      placeholder="3423H"
                    />
                  </td>
                  <td>
                    <input
                      value={item.color}
                      onChange={(e) => updateItem(index, "color", e.target.value)}
                      placeholder="Ivory"
                    />
                  </td>
                  <td>
                    <input
                      value={item.size}
                      onChange={(e) => updateItem(index, "size", e.target.value)}
                      placeholder="36"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={item.expectedQty}
                      onChange={(e) =>
                        updateItem(index, "expectedQty", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={item.receivedQty}
                      onChange={(e) =>
                        updateItem(index, "receivedQty", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={item.lostQty}
                      onChange={(e) => updateItem(index, "lostQty", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={item.damagedQty}
                      onChange={(e) =>
                        updateItem(index, "damagedQty", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={item.unitCostUsd}
                      onChange={(e) =>
                        updateItem(index, "unitCostUsd", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={item.targetSellingPriceMmk}
                      onChange={(e) =>
                        updateItem(
                          index,
                          "targetSellingPriceMmk",
                          e.target.value,
                        )
                      }
                    />
                  </td>
                  <td>
                    <button
                      className="profitCostRemoveBtn"
                      onClick={() => removeItem(index)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button className="profitCostSaveBtn" onClick={saveBatch} disabled={saving}>
          {saving ? "Saving..." : "Save Import Cost Batch"}
        </button>
      </div>

      {saveResult && (
        <div className="profitCostResult">
          <h2>Saved Result</h2>
          <div className="profitCostResult__grid">
            <div>
              <span>Total Cost</span>
              <strong>{formatMoney(saveResult.calculations?.totalCostMmk)} Ks</strong>
            </div>
            <div>
              <span>FX Gain / Loss</span>
              <strong>{formatMoney(saveResult.calculations?.fxGainLossMmk)} Ks</strong>
            </div>
            <div>
              <span>Lost Cost</span>
              <strong>{formatMoney(saveResult.calculations?.lostCostMmk)} Ks</strong>
            </div>
            <div>
              <span>Damage Cost</span>
              <strong>{formatMoney(saveResult.calculations?.damagedCostMmk)} Ks</strong>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) return content;

  return <Layout title="Profit Cost">{content}</Layout>;
}
